import React, { ChangeEvent, useCallback, useEffect, useState } from 'react'

import { useRouter } from 'next/router'
import { Box, Button, Flex, Text, Link, InputGroup } from '@clear/design-system'
import { Map } from '@components/storybook'
import PageContent from '@components/layout/PageContent'
import Spinner from '@components/shared/Spinner'
import TopBanner from '@components/shared/TopBanner'
import getEnrollmentLocations from '@api/getEnrollmentLocations'
import {
  determineMapZoomLevel,
  getNearbyEnrollmentLocations,
  EnrollmentLocation,
} from '@utils/getNearbyEnrollmentLocations'
import { PlanTypes, useEnrollmentContext } from '@components/EnrollmentContext'
import { HeadTitle } from '@components/layout/HeadTitle'
import { getGlobalErrorMessage } from '@utils/apiErrorMessages'
import { ErrorMessage } from '@components/layout/Form'
import { useJsApiLoader } from '@react-google-maps/api'
import { GeoLocate } from '@components/shared/GeoLocate'
import { LaneInfo } from '@components/shared/LaneInfo'

const LocationsPage: React.FC = () => {
  const router = useRouter()
  const { setSelectedPlan } = useEnrollmentContext()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [errors] = useState({})
  const [searchLocation, setSearchLocation] = useState(null)
  const [searchInput, setSearchInput] = useState('')
  const [mapSearchError, setMapSearchError] = useState(false)
  const [enrollmentLocations, setEnrollmentLocations] =
    useState<Record<string, any>>()
  const [locationsWithinRange, setLocationsWithinRange] = useState<
    EnrollmentLocation[]
  >([])

  const handleSearch = useCallback(
    async (searchCriteria: string) => {
      setIsLoading(true)
      setMapSearchError(false)
      setLocationsWithinRange([])
      try {
        setIsLoading(true)
        const { enrollmentLocationsNearSearch } =
          await getNearbyEnrollmentLocations(
            enrollmentLocations,
            searchInput || searchCriteria
          )
        const sortedEnrollmentLocationsNearSearch =
          enrollmentLocationsNearSearch.sort((a, b) => a.distance - b.distance)
        setLocationsWithinRange(sortedEnrollmentLocationsNearSearch)
        setSearchLocation(
          enrollmentLocationsNearSearch[0]?.location || searchInput
        )
        setIsLoading(false)
        setSearchInput('')
      } catch (e) {
        console.log(e)
        setIsLoading(false)
        setMapSearchError(true)
        setSearchInput('')
      }
    },
    [
      setLocationsWithinRange,
      setSearchLocation,
      setMapSearchError,
      searchInput,
      enrollmentLocations,
      setIsLoading,
    ]
  )

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || '',
  })

  const handleSelectPlan = async (plan: string) => {
    setSelectedPlan(plan)
    await router.push(
      `/enroll/document-check${
        router.query.test === 'true' ? '?test=true' : ''
      }`
    )
  }

  const updateSearchBar = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setSearchInput(e.target.value)
    },
    [setSearchInput]
  )

  useEffect(() => {
    const fetchData = async () => {
      const response = await getEnrollmentLocations()
      setEnrollmentLocations(response)
    }
    fetchData()
  }, [])

  useEffect(() => {
    if (
      router.query.code &&
      enrollmentLocations &&
      !locationsWithinRange.length &&
      !isLoading &&
      isLoaded
    ) {
      const codeQuery =
        typeof router.query.code === 'string' ? router.query.code : ''
      setSearchInput(codeQuery)
      handleSearch(codeQuery)
    }
  }, [
    enrollmentLocations,
    handleSearch,
    isLoaded,
    isLoading,
    locationsWithinRange.length,
    router.query.code,
  ])

  const geoLocateSuccessHandler = (position: GeolocationPosition) => {
    handleSearch(`${position.coords.latitude},${position.coords.longitude}`)
  }

  const handleKeyDown = (e: any) => {
    if (e.key === 'Enter') {
      handleSearch(searchInput)
    }
  }
  return (
    <>
      <HeadTitle
        title="TSA&nbsp;PreCheck® Enrollment Provided by CLEAR Locations"
        description="Find a full list of airports with both TSA&nbsp;PreCheck® and CLEAR. Move more quickly & more securely through airport security today."
      />
      <Flex flexDirection="column">
        <TopBanner />
        <PageContent mt={[8, 11]}>
          <Box textAlign="center" mb={4}>
            <Text
              as="h1"
              variant="display"
              color="brandDarkBlue"
              fontSize={[5, 8]}
              display="inline-block"
            >
              Find an Enrollment Location
            </Text>
          </Box>
          <>
            <Flex
              alignItems="center"
              justifyContent="center"
              id="locations-search-bar"
              mb={7}
              width="full"
              flexDirection={['column', 'row']}
            >
              <InputGroup
                icon="search"
                variant="default"
                onKeyDown={(e) => {
                  handleKeyDown(e)
                }}
                iconClick={() => handleSearch(searchInput)}
                onChange={updateSearchBar}
                label="Search for locations"
                value={searchInput}
                placeholder="Zip / City / Airport Code"
                autoComplete="off"
                data-cy="searchBar"
              />
              <GeoLocate onSuccessHandler={geoLocateSuccessHandler} />
            </Flex>
            {mapSearchError && (
              <Box as="p" textAlign="center" color="utilityDarkErrorRed">
                Sorry, that's not a valid search. Please try again.
              </Box>
            )}
            {locationsWithinRange.length ? (
              <Flex flexDirection={['column-reverse', 'row']}>
                <Box width={['full', '40%']} pr={6}>
                  {locationsWithinRange?.map((location: EnrollmentLocation) => (
                    <Box key={location?.airportCode}>
                      <Text as="h5" fontSize={4} mb={2}>
                        {location?.name}
                      </Text>
                      <Text
                        fontSize={0}
                        color="neutralsTextGray"
                        display="inline-block"
                      >
                        {location?.distance} miles away •{' '}
                        <Link
                          href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURI(
                            location.address
                          )}`}
                          target="_blank"
                        >
                          {location?.address}
                        </Link>
                      </Text>
                      <br />
                      {location?.open && (
                        <>
                          <Text
                            fontSize={0}
                            color="neutralsTextGray"
                            display="inline-block"
                          >
                            Open Daily
                          </Text>
                          <br />
                        </>
                      )}
                      <Text fontSize={0} color="utilityDarkErrorRed">
                        {location?.important_info}
                      </Text>
                      <Text
                        className="flex flex-col"
                        fontSize={0}
                        color="neutralsPlaceholderGray"
                      >
                        {location?.lanes.map(
                          ({
                            name,
                            description,
                            isAccessible,
                            availability,
                            accessibilityMessage,
                            holidayMessage,
                            id,
                          }) => (
                            <LaneInfo
                              key={id}
                              id={id}
                              name={name}
                              description={description}
                              accessibilityMessage={accessibilityMessage}
                              isAccessible={isAccessible}
                              availability={availability}
                              holidayMessage={holidayMessage}
                            />
                          )
                        )}
                      </Text>
                    </Box>
                  ))}
                </Box>
                {isLoaded ? (
                  <Box width={['full', '60%']} height="24rem" mb={[6, 0]}>
                    <Map
                      center={searchLocation}
                      zoom={determineMapZoomLevel(locationsWithinRange)}
                      locationMarkers={locationsWithinRange}
                      full
                    />
                  </Box>
                ) : (
                  <>Loading google maps</>
                )}
              </Flex>
            ) : null}
            {!locationsWithinRange.length &&
              searchLocation &&
              !mapSearchError &&
              !isLoading && (
                <Box alignItems="start" ml={4} mt={3} display="flex">
                  <Box
                    as="img"
                    src="/map-pin-not-found.svg"
                    alt="pin-not-found"
                  />
                  <Box ml={3}>
                    <Text fontSize={4} display="block" mb={1} id="no-locations">
                      No locations found
                    </Text>
                    <Text fontSize={1}>
                      Please adjust your search for better results.
                    </Text>
                  </Box>
                </Box>
              )}
          </>

          <Flex
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            pb={3}
            mt={7}
          >
            {locationsWithinRange && locationsWithinRange[0]?.distance > 40 && (
              <Flex
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                mb={6}
              >
                <Text fontSize={4} display="block" mb={1}>
                  Need a closer location?
                </Text>
                <Text fontSize={2}>
                  Visit the{' '}
                  <Link
                    target="_blank"
                    href="https://www.tsa.gov/precheck/enrollment-centers"
                  >
                    TSA website
                  </Link>{' '}
                  for more enrollment location options.
                </Text>
              </Flex>
            )}
            <Text
              variant="display"
              fontSize={[2, 4]}
              mb={6}
              color="neutralsTextGray"
            >
              To expedite in-person enrollment, fill out an application online
              before heading to an enrollment location near you. If you choose
              not to complete an online application, you can still enroll
              in-person at one of our enrollment locations—no appointment
              needed. Be sure to bring any required documentation with you. Find
              out what to bring under the Application section of FAQs{' '}
              <Text as="a" href="/help" color="brandMidnightBlue">
                here
              </Text>
              .
            </Text>
            <Button
              text="Apply"
              textProps={{ variant: 'regular' }}
              mb={1}
              onClick={() => handleSelectPlan(PlanTypes.ENROLL)}
              data-cy="enroll-button"
            />
          </Flex>
          {isLoading && <Spinner />}
          {getGlobalErrorMessage(errors) && (
            <ErrorMessage mb={6}>{getGlobalErrorMessage(errors)}</ErrorMessage>
          )}
        </PageContent>
      </Flex>
    </>
  )
}

export default LocationsPage
